import dayjs from "dayjs";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useDeleteNISParticipantActivityTimeLineMutation,
  useGetNISParticipantActivitiesList,
} from "../../../../../../../../api/services/nisParticipants/nisParticipantsApis";
import {
  useDeleteParticipantActivityTimeLineMutation,
  useGetParticipantActivitiesList,
} from "../../../../../../../../api/services/program/programApis";
import {
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiDateFieldFormController,
  MuiDoubleLabel,
  MuiGrid,
  MuiSelect,
  WarningConfirmationModal,
} from "../../../../../../../../components";
import PermissionWrapper from "../../../../../../../../components/permissionWrapper/PermissionWrapper";
import {
  TimelineActivityDataItem,
  TimelineActivityType,
  ViewType,
} from "../../../../../../../../components/timelineActivity/TimelineActivity";
import { TimelineActivityTable } from "../../../../../../../../components/timelineActivityTable/TimelineActivityTable";
import {
  ACTIVITY_TIMELINE_TYPE,
  chipKey,
  getCustomDateFormatWithTime,
  labelKey,
  MODULE_PERMISSIONS,
  viewMoreKey,
} from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry, getDateFormatByCountryWithTime } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  convertToUserFormatWithLocalTime,
  getFullName,
  getValueOrBlank,
  getValueOrBlankArray,
  getYesterdayDate,
  isDateInRange,
  isEmpty,
  isNotEmpty,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../../../hooks/useToast";
import { siteCoreContentLoadingSelector } from "../../../../../../../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import {
  AcrossCountry,
  ParticipantActivityResponse,
  ParticipantActivityTimelineFilterFormKey,
  ParticipantActivityTimelineFilterFormType,
  ParticipantTabFormKey,
  ShowFilterAllKey,
  ShowFilterDurationKey,
  ShowFilterLastMonthKey,
  showFilterThisMonthKey,
  WithInTheCountry,
} from "../../../../../../modelsAndSchemas/Models";
import { activityTimelineSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { ParticipantAddActivity } from "../participantAddActivity/ParticipantAddActivity";

interface ParticipantActivityTimelineFormProps {
  pageId: string;
  isEditProgram?: boolean;
  programId: string;
  displayAddBtn?: boolean;
  isFromCoachPortal?: boolean;
  showMoreOptions?: boolean;
  isFromGenericParticipant?: boolean;
  nextContactDate?: string;
  showNextContactDate?: boolean;
  isFromNISParticipant?: boolean;
  nisParticipantStatus?: string;
  renderCustomNextContactDate?: () => React.JSX.Element;
  hasDeletePermission?: boolean;
}

const getShowMoreOptions = (
  activity: ParticipantActivityResponse,
  props: ParticipantActivityTimelineFormProps,
) =>
  (activity.isEditable && props.isEditProgram) ||
  (props.isFromCoachPortal && props.showMoreOptions) ||
  (activity.isEditable && props.isFromNISParticipant && props.displayAddBtn);

const isNotClosureType = (timelineType: number) =>
  timelineType !== ACTIVITY_TIMELINE_TYPE.participantStatusClosed &&
  timelineType !== ACTIVITY_TIMELINE_TYPE.participantStatusConCon &&
  timelineType !== ACTIVITY_TIMELINE_TYPE.participantStatusActive;

const ParticipantActivityTimeline: FC<ParticipantActivityTimelineFormProps> = (
  props,
) => {
  const {
    pageId,
    isEditProgram,
    programId,
    displayAddBtn = true,
    isFromCoachPortal = false,
    isFromGenericParticipant = false,
    nextContactDate,
    showNextContactDate = true,
    isFromNISParticipant = false,
    nisParticipantStatus,
    renderCustomNextContactDate,
    hasDeletePermission = true,
  } = props;
  const {
    fetchLabel,
    commonLabels,
    fetchValueByKeyOfDropdown,
    fetchDropdownValues,
  } = useSiteCore();
  const { gridSizeMedium: gridSize } = useGridContainer();
  const { commonARIALabel } = useNavigationSiteCore();
  const defaultValueDefault = {
    [ParticipantActivityTimelineFilterFormKey.show]: ShowFilterAllKey,
  };
  const indexRef = useRef<string>("");
  const { isMobile } = useMediaQueries();

  const { control, getValues, setValue, watch } =
    useForm<ParticipantActivityTimelineFilterFormType>({
      mode: "all",
      defaultValues: defaultValueDefault,
    });

  const { control: parentFormControl, getValues: getParentFormValues } =
    useFormContext();

  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const [isOpenActivity, setIsOpenActivity] = useState(false);

  const tableRef = useRef<{ resetPagination: () => void }>(null);

  const [activityData, setActivityData] = useState<TimelineActivityType[]>([]);
  const [isEditActivity, setIsEditActivity] = useState(false);

  const { show: selectedFilter } = getValues();

  const yesterdayDate = getYesterdayDate();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const userCountryDateFormatTime = getDateFormatByCountryWithTime(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const [filteredActivityData, setFilteredActivityData] = useState<
    TimelineActivityType[]
  >([]);

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

  const [editActivityData, setEditActivityData] =
    useState<ParticipantActivityResponse>();

  const getLabel = (key: string) => {
    return fetchLabel(activityTimelineSiteCoreKeys.activityTimeline, key);
  };

  const onCreateActivityClick = () => {
    setIsOpenActivity(true);
  };
  const dispatch = useDispatch();

  const { displayToast } = useToast();

  const {
    data: activityTimelineData,
    isFetching: isActivityTimelinePending,
    isError: isActivityTimelineError,
    isSuccess: isActivityTimelineSuccess,
    refetchWithParams: getActivityTimelineData,
  } = useGetParticipantActivitiesList();

  const {
    data: nisActivityTimelineData,
    isFetching: isNISActivityTimelinePending,
    isError: isNISActivityTimelineError,
    isSuccess: isNISActivityTimelineSuccess,
    refetchWithParams: getNISActivityTimelineData,
  } = useGetNISParticipantActivitiesList();

  const {
    mutateAsync: deleteNISParticipantActivity,
    isPending: isDeleteNISParticipantActivityPending,
    isSuccess: isDeleteNISParticipantActivitySuccess,
    isError: isDeleteNISParticipantActivityError,
  } = useDeleteNISParticipantActivityTimeLineMutation();

  const {
    mutateAsync: deleteParticipantActivity,
    isPending: isDeleteParticipantActivityPending,
    isSuccess: isDeleteParticipantActivitySuccess,
    isError: isDeleteParticipantActivityError,
  } = useDeleteParticipantActivityTimeLineMutation();

  const currentActivityTimelineData = useMemo(
    () =>
      isFromNISParticipant ? nisActivityTimelineData : activityTimelineData,
    [isFromNISParticipant, nisActivityTimelineData, activityTimelineData],
  );

  const isCurrentActivityTimelineSuccess = useMemo(
    () =>
      isFromNISParticipant
        ? isNISActivityTimelineSuccess
        : isActivityTimelineSuccess,
    [
      isFromNISParticipant,
      isNISActivityTimelineSuccess,
      isActivityTimelineSuccess,
    ],
  );

  useEffect(() => {
    if (programId) getActivityData();
  }, [programId]);

  const getActivityData = () => {
    if (isFromNISParticipant) getNISActivityTimelineData(programId);
    else
      getActivityTimelineData({
        id: programId,
        isFromGenericParticipant: isFromGenericParticipant,
        isFromNISParticipant: isFromNISParticipant,
      });
  };

  useEffect(() => {
    if (
      isActivityTimelinePending ||
      isDeleteParticipantActivityPending ||
      isNISActivityTimelinePending ||
      isDeleteNISParticipantActivityPending
    ) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isActivityTimelinePending,
    isDeleteParticipantActivityPending,
    isNISActivityTimelinePending,
    isDeleteNISParticipantActivityPending,
  ]);

  useEffect(() => {
    if (
      isActivityTimelineError ||
      isDeleteParticipantActivityError ||
      isNISActivityTimelineError ||
      isDeleteNISParticipantActivityError
    ) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
    }
  }, [
    isActivityTimelineError,
    isDeleteParticipantActivityError,
    isNISActivityTimelineError,
    isDeleteNISParticipantActivityError,
  ]);

  const getFormattedActivityTitle = (
    activityTitle: string,
    timeLineType: number,
  ): string => {
    const intakeStatusTypeMap: Record<
      number,
      { labelKey: string; listKey: string }
    > = {
      [ACTIVITY_TIMELINE_TYPE.intakeStatus]: {
        labelKey: activityTimelineSiteCoreKeys.intakeStatusTitle,
        listKey: activityTimelineSiteCoreKeys.intakeStatusList,
      },
      [ACTIVITY_TIMELINE_TYPE.biosStatus]: {
        labelKey: activityTimelineSiteCoreKeys.bioStatusTitle,
        listKey: activityTimelineSiteCoreKeys.bioStatusList,
      },
      [ACTIVITY_TIMELINE_TYPE.chemistryMeetingStatus]: {
        labelKey: activityTimelineSiteCoreKeys.chemistryMeetingStatusTitle,
        listKey: activityTimelineSiteCoreKeys.chemistryMeetingStatusList,
      },
    };

    const titleMap: Record<string, string> = {
      [AcrossCountry]: activityTimelineSiteCoreKeys.participantTransferred,
      [WithInTheCountry]: activityTimelineSiteCoreKeys.mobilityToOutplacement,
    };

    if (intakeStatusTypeMap[timeLineType]) {
      const { labelKey, listKey } = intakeStatusTypeMap[timeLineType];
      return `${getLabel(labelKey)} - ${fetchValueByKeyOfDropdown(
        activityTimelineSiteCoreKeys.activityTimeline,
        listKey,
        getValueOrBlank(activityTitle),
      )}`;
    }

    if (timeLineType === ACTIVITY_TIMELINE_TYPE.selectedCoach) {
      return getLabel(activityTimelineSiteCoreKeys.coachSelectedTitle);
    }

    if (timeLineType === ACTIVITY_TIMELINE_TYPE.nisParticipantStatusConverted) {
      return getLabel(activityTimelineSiteCoreKeys.convertedToP3);
    }
    if (timeLineType === ACTIVITY_TIMELINE_TYPE.nisParticipantStatusChanged) {
      return fetchValueByKeyOfDropdown(
        activityTimelineSiteCoreKeys.activityTimeline,
        activityTimelineSiteCoreKeys.nisParticipantStatusList,
        getValueOrBlank(activityTitle),
      );
    }

    if (titleMap[activityTitle]) {
      return getLabel(titleMap[activityTitle]);
    }

    return activityTitle;
  };

  const getDateTimeFormat = () =>
    isFromNISParticipant
      ? getCustomDateFormatWithTime(userCountryDateFormat)
      : userCountryDateFormatTime;

  const getTimeStampValue = (activityDate?: string) => {
    if (!activityDate || isEmpty(activityDate)) return "";
    return convertToUserFormatWithLocalTime(
                activityDate,
                getDateTimeFormat(),
                loggedInUser?.timeZone,
              )
  };

  useEffect(() => {
    if (
      !isCurrentActivityTimelineSuccess ||
      isEmpty(currentActivityTimelineData) ||
      siteCoreLoading
    ) {
      setActivityData([]);
      return;
    }

    const localData: TimelineActivityType[] = getValueOrBlankArray(
      currentActivityTimelineData,
    ).map((activity: ParticipantActivityResponse) => ({
      timeStamp: getTimeStampValue(activity.activityDate),
      id: activity.id,
      title: getFormattedActivityTitle(activity.title, activity.timelineType),
      creator: activity.createdBy,
      isCollapsible: activity.isExpandable,
      expandableData: transformActivityData(activity),
      showMoreOptions: getShowMoreOptions(activity, props),
      isNotClosureType: isNotClosureType(activity.timelineType),
    }));

    setActivityData(localData);
  }, [
    isCurrentActivityTimelineSuccess,
    currentActivityTimelineData,
    siteCoreLoading,
  ]);

  useEffect(() => {
    if (
      isDeleteParticipantActivitySuccess ||
      isDeleteNISParticipantActivitySuccess
    ) {
      getActivityData();
      displayToast(
        getLabel(activityTimelineSiteCoreKeys.activityDeletedToastMessage),
      );
    }
  }, [
    isDeleteParticipantActivitySuccess,
    isDeleteNISParticipantActivitySuccess,
  ]);

  const getDurationValue = (duration?: string, listKey?: string) => {
    if (!duration && isEmpty(duration)) {
      return "";
    }
    if (listKey && isNotEmpty(listKey)) {
      const durationValue = fetchValueByKeyOfDropdown(
        activityTimelineSiteCoreKeys.activityTimeline,
        listKey,
        getValueOrBlank(duration),
      );
      return isNotEmpty(durationValue)
        ? `${durationValue} ${commonLabels.minutes}`
        : "";
    }
    return `${duration} ${commonLabels.minutes}`;
  };

  const transformActivityData = (
    activityData: ParticipantActivityResponse,
  ): TimelineActivityDataItem[] => {
    const {
      activityName,
      modality,
      topics,
      notes,
      title,
      createdBy,
      programTemplateName,
      transferredCountryName,
      solutionOfferingsName,
      timelineType,
      primaryClosingReason,
      secondaryClosingReason,
      actualDurationInMinutes,
      defaultDurationInMinutes,
      onBehalfOfCoach,
      activityDate,
    } = activityData;
    const isFromCountryTitle = title === WithInTheCountry;

    const isAcrossCountryTitle = title === AcrossCountry;

    // Helper function to create notes field with appropriate type
    const getNotesField = () => {
      return {
        key: activityTimelineSiteCoreKeys.notesLabel,
        value: getValueOrBlank(notes),
        type: viewMoreKey as ViewType,
      };
    };

    const getBaseTransferLabels = () => [
      {
        key: activityTimelineSiteCoreKeys.nextView,
        value: getValueOrBlank(solutionOfferingsName),
      },
      {
        key: activityTimelineSiteCoreKeys.compatibleProgramTemplate,
        value: getValueOrBlank(programTemplateName),
      },
      {
        key: activityTimelineSiteCoreKeys.createdBy,
        value: getValueOrBlank(createdBy),
      },
    ];

    const getFromCountryLabels = () => getBaseTransferLabels();

    const getAcrossCountryLabels = () => [
      {
        key: activityTimelineSiteCoreKeys.transferredCountry,
        value: getValueOrBlank(transferredCountryName),
      },
      ...getBaseTransferLabels(),
    ];

    const getNISAutomatedStatusLabels = () => [
      {
        key: activityTimelineSiteCoreKeys.nisStatus,
        value: getValueOrBlank(title),
        listKey: activityTimelineSiteCoreKeys.nisParticipantStatusList,
      },
      getNotesField(),
    ];

    const getNISConvertedStatusLabels = () => [
      {
        key: activityTimelineSiteCoreKeys.nisConvertedDate,
        value: convertToUserFormatWithLocalTime(
          activityDate,
          getCustomDateFormatWithTime(userCountryDateFormat),
        ),
      },
      {
        key: activityTimelineSiteCoreKeys.nisConvertedBy,
        value: getValueOrBlank(createdBy),
      },
      {
        key: activityTimelineSiteCoreKeys.programKey,
        value: getValueOrBlank(activityData?.program?.keyNumber),
      },
    ];

    const getDefaultLabels = () => [
      {
        key: activityTimelineSiteCoreKeys.activityType,
        value: getValueOrBlank(activityName),
      },
      {
        key: activityTimelineSiteCoreKeys.modality,
        value: getValueOrBlank(modality),
        listKey: activityTimelineSiteCoreKeys.modalityList,
      },
      {
        key: activityTimelineSiteCoreKeys.topic,
        value:
          topics?.map((topic: { name: string; id: string }) => ({
            label: topic.name,
            value: topic.id,
          })) ?? [],
      },
      {
        key: activityTimelineSiteCoreKeys.defaultDuration,
        value: getDurationValue(defaultDurationInMinutes?.toString()),
      },
      {
        key: activityTimelineSiteCoreKeys.actualDuration,
        value: getDurationValue(
          actualDurationInMinutes,
          activityTimelineSiteCoreKeys.actualDurationMinutesList,
        ),
      },
      {
        key: activityTimelineSiteCoreKeys.activityDate,
        value: convertToUserFormat(activityDate, userCountryDateFormat),
      },
      ...(isFromNISParticipant
        ? []
        : [
            {
              key: activityTimelineSiteCoreKeys.onBehalfOfCoachId,
              value: getValueOrBlank(
                getFullName(
                  onBehalfOfCoach?.firstName,
                  onBehalfOfCoach?.lastName,
                ),
              ),
            },
          ]),
      getNotesField(),
    ];
    const getClosingTypeLabels = () => [
      {
        key: activityTimelineSiteCoreKeys.primaryClosingReason,
        value: getValueOrBlank(primaryClosingReason),
      },
      {
        key: activityTimelineSiteCoreKeys.secondaryClosingReason,
        value: getValueOrBlank(secondaryClosingReason),
      },
    ];

    let labels: {
      key: string;
      value: string | Array<{ label: string; value: string }>;
      listKey?: string;
      type?: ViewType;
    }[];

    if (timelineType === ACTIVITY_TIMELINE_TYPE.participantStatusClosed) {
      labels = getClosingTypeLabels();
    } else if (
      timelineType === ACTIVITY_TIMELINE_TYPE.nisParticipantStatusChanged
    ) {
      labels = getNISAutomatedStatusLabels();
    } else if (
      timelineType === ACTIVITY_TIMELINE_TYPE.nisParticipantStatusConverted
    ) {
      labels = getNISConvertedStatusLabels();
    } else if (isFromCountryTitle) {
      labels = getFromCountryLabels();
    } else if (isAcrossCountryTitle) {
      labels = getAcrossCountryLabels();
    } else {
      labels = getDefaultLabels();
    }

    return labels.filter(Boolean).map(({ key, value, listKey, type }) => {
      if (Array.isArray(value)) {
        return {
          type: type ?? chipKey,
          label: getLabel(key),
          value,
        };
      }

      return {
        type: type ?? labelKey,
        label: getLabel(key),
        value: listKey
          ? fetchValueByKeyOfDropdown(
              activityTimelineSiteCoreKeys.activityTimeline,
              listKey,
              getValueOrBlank(value),
            )
          : value,
      };
    });
  };

  useEffect(() => {
    applyFilters();
  }, [watch(ParticipantActivityTimelineFilterFormKey.show), activityData]);

  const onGoBtnClick = () => {
    applyFilters();
  };

  const applyFilters = () => {
    const currentDate = dayjs();
    const startOfCurrentMonth = currentDate.startOf("month");
    const endOfCurrentMonth = currentDate.endOf("month");
    const startOfLastMonth = currentDate.subtract(1, "month").startOf("month");
    const endOfLastMonth = currentDate.subtract(1, "month").endOf("month");

    resetPagination();

    if (selectedFilter !== ShowFilterDurationKey) {
      setValue(ParticipantActivityTimelineFilterFormKey.fromDate, "");
      setValue(ParticipantActivityTimelineFilterFormKey.toDate, "");
    }
    const fromDate = getValues(
      ParticipantActivityTimelineFilterFormKey.fromDate,
    );
    const toDate = getValues(ParticipantActivityTimelineFilterFormKey.toDate);

    const filteredData = activityData.filter((activity) => {
      const activityDate = dayjs(activity.timeStamp, userCountryDateFormat);

      if (selectedFilter === ShowFilterAllKey) {
        return true; // Show all activities
      } else if (selectedFilter === showFilterThisMonthKey) {
        // Show activities from this month
        return isDateInRange(
          activityDate,
          startOfCurrentMonth,
          endOfCurrentMonth,
        );
      } else if (selectedFilter === ShowFilterLastMonthKey) {
        // Show activities from last month
        return isDateInRange(activityDate, startOfLastMonth, endOfLastMonth);
      } else if (
        selectedFilter === ShowFilterDurationKey &&
        fromDate &&
        toDate
      ) {
        // Show activities between selected 'fromDate' and 'toDate'
        const dayjsFromDate = dayjs(fromDate, userCountryDateFormat);
        const dayjsToDate = dayjs(toDate, userCountryDateFormat);
        return isDateInRange(activityDate, dayjsFromDate, dayjsToDate);
      } else {
        return true;
      }
    });

    setFilteredActivityData(filteredData);
  };

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const handleEditActivity = (id: string) => {
    const activity = currentActivityTimelineData?.find(
      (activity: ParticipantActivityResponse) => activity.id === id,
    );

    if (activity) {
      setIsEditActivity(true);
      setEditActivityData(activity);
      setIsOpenActivity(true);
    }
  };

  const handleDeleteActivity = (id: string) => {
    indexRef.current = id;
    setShowDeleteConfirmModal(true);
  };

  const handleDeleteClick = () => {
    if (isNotEmpty(indexRef.current)) {
      let idToDelete = indexRef.current;
      isFromNISParticipant
        ? deleteNISParticipantActivity(idToDelete)
        : deleteParticipantActivity(idToDelete);
      handleModalClose();
    }
  };

  const handleModalClose = () => {
    if (showDeleteConfirmModal) {
      setShowDeleteConfirmModal(false);
    }
  };

  const handleActivityModalClose = () => {
    setIsOpenActivity(false);
    setIsEditActivity(false);
    setEditActivityData(undefined);
  };

  const getDateValueForViewScreen = () => {
    return convertToUserFormat(nextContactDate, userCountryDateFormat);
  };

  const getDateValue = (key: keyof typeof ParticipantTabFormKey) => {
    if (isFromCoachPortal && isNotEmpty(nextContactDate)) {
      const date = convertToUserFormat(nextContactDate, userCountryDateFormat);
      return dayjs(date);
    } else {
      const value = isNotEmpty(getParentFormValues(key))
        ? (getParentFormValues(key) as unknown as string)
        : null;
      return dayjs(value, userCountryDateFormat);
    }
  };

  const isViewOnly = !isEditProgram && !displayAddBtn;

  const renderDateFieldOrLabel = () => {
    if (isViewOnly) {
      return (
        <MuiGrid size={gridSize}>
          <MuiDoubleLabel
            label={getLabel(activityTimelineSiteCoreKeys.nextContactDate)}
            replaceEmptyWithDash={isEmpty(nextContactDate)}
            value={getDateValueForViewScreen()}
          />
        </MuiGrid>
      );
    } else if (isFromCoachPortal) {
      return renderCustomNextContactDate?.();
    } else {
      return (
        <MuiGrid size={gridSize}>
          <MuiDateFieldFormController
            control={parentFormControl}
            name={ParticipantTabFormKey.nextContactDate}
            id={`${pageId}-${ParticipantTabFormKey.nextContactDate}`}
            value={getDateValue(ParticipantTabFormKey.nextContactDate)}
            label={getLabel(activityTimelineSiteCoreKeys.nextContactDate)}
            valueFormat={userCountryDateFormat}
            disablePast
            clearable={true}
            className="date-picker"
            requiredARIALabel={commonARIALabel.required}
            selectDateARIALabel={commonARIALabel.selectDate}
            format={userCountryDateFormat}
          />
        </MuiGrid>
      );
    }
  };

  const getPermissionForAddButton = () => {
    if (isFromNISParticipant) {
      return MODULE_PERMISSIONS.NIS_CREATE;
    }
    if (isFromCoachPortal) {
      return MODULE_PERMISSIONS.COACH_PORTAL_ADD_PARTICIPANT_ACTIVITY_TIMELINE;
    }
    return MODULE_PERMISSIONS.ADD_PARTICIPANT_ACTIVITY_TIMELINE;
  };

  return (
    <MuiBox
      className="activity-timeline"
      data-testid="participant-activity-timeline"
    >
      {showNextContactDate && (
        <MuiBox className="next-contact-date-wrapper">
          {renderDateFieldOrLabel()}
        </MuiBox>
      )}
      <MuiBox className="filter-wrapper">
        <MuiBox className="modality">
          <FormController
            control={control}
            name={ParticipantActivityTimelineFilterFormKey.show}
          >
            <MuiSelect
              label={getLabel(activityTimelineSiteCoreKeys.show)}
              options={fetchDropdownValues(
                activityTimelineSiteCoreKeys.activityTimeline,
                activityTimelineSiteCoreKeys.showFilterList,
              )}
              isHorizontalLabel={!isMobile}
              value=""
              id={`${pageId}-${ParticipantActivityTimelineFilterFormKey.show}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiBox>

        {displayAddBtn && (
          <PermissionWrapper permission={getPermissionForAddButton()}>
            <MuiBox className="add-activity-wrapper">
              <MuiButtonSecondary
                data-testid={`${pageId}-create-activity`}
                onClick={onCreateActivityClick}
              >
                {getLabel(activityTimelineSiteCoreKeys.addActivityLink)}
              </MuiButtonSecondary>
            </MuiBox>
          </PermissionWrapper>
        )}
      </MuiBox>
      {selectedFilter === ShowFilterDurationKey && (
        <MuiBox className="duration-picker-wrapper">
          <MuiGrid container>
            <MuiGrid size={gridSize}>
              <MuiDateFieldFormController
                control={control}
                name={ParticipantActivityTimelineFilterFormKey.fromDate}
                label={getLabel(activityTimelineSiteCoreKeys.fromDateLabel)}
                valueFormat={userCountryDateFormat}
                format={userCountryDateFormat}
                className="date-picker"
                maxDate={yesterdayDate}
                id={`${pageId}-${ParticipantActivityTimelineFilterFormKey.fromDate}`}
                disableManualInput
                value={
                  getValues(
                    ParticipantActivityTimelineFilterFormKey.fromDate,
                  ) !== ""
                    ? dayjs(
                        getValues(
                          ParticipantActivityTimelineFilterFormKey.fromDate,
                        ),
                        userCountryDateFormat,
                      )
                    : null
                }
                requiredARIALabel={commonARIALabel.required}
                selectDateARIALabel={commonARIALabel.selectDate}
              />
            </MuiGrid>

            <MuiGrid size={gridSize}>
              <MuiDateFieldFormController
                control={control}
                name={ParticipantActivityTimelineFilterFormKey.toDate}
                label={getLabel(activityTimelineSiteCoreKeys.toDateLabel)}
                valueFormat={userCountryDateFormat}
                format={userCountryDateFormat}
                className="date-picker"
                disableFuture
                id={`${pageId}-${ParticipantActivityTimelineFilterFormKey.toDate}`}
                disableManualInput
                value={
                  getValues(ParticipantActivityTimelineFilterFormKey.toDate) !==
                  ""
                    ? dayjs(
                        getValues(
                          ParticipantActivityTimelineFilterFormKey.toDate,
                        ),
                        userCountryDateFormat,
                      )
                    : null
                }
                requiredARIALabel={commonARIALabel.required}
                selectDateARIALabel={commonARIALabel.selectDate}
              />
            </MuiGrid>
            <MuiGrid size={gridSize} className="go-btn-grid">
              <MuiButtonPrimary
                onClick={onGoBtnClick}
                id={`${pageId}-duration-go-btn`}
              >
                {commonLabels.go}
              </MuiButtonPrimary>
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
      )}

      <TimelineActivityTable
        ref={tableRef}
        activityData={filteredActivityData}
        handleEdit={handleEditActivity}
        handleDelete={handleDeleteActivity}
        pageId={pageId}
        noDataInApi={isCurrentActivityTimelineSuccess && isEmpty(activityData)}
        emptyMessage={getLabel(
          activityTimelineSiteCoreKeys.noActivitiesAvailableText,
        )}
        hasDeletePermission={hasDeletePermission}
      />

      {isOpenActivity && (
        <ParticipantAddActivity
          show={isOpenActivity}
          handleCloseModal={handleActivityModalClose}
          pageId={pageId}
          programId={programId}
          getListOfActivitiesApi={() => getActivityData()}
          isEditActivity={isEditActivity}
          editActivityData={editActivityData}
          isFromNISParticipant={isFromNISParticipant}
          nisParticipantStatus={nisParticipantStatus}
        />
      )}

      {showDeleteConfirmModal && (
        <WarningConfirmationModal
          show={showDeleteConfirmModal}
          modalTitle={commonLabels.confirmation}
          onClose={handleModalClose}
          onPrimaryButtonClick={handleDeleteClick}
          labelContent={getLabel(
            activityTimelineSiteCoreKeys.deleteActivityConfirmationText,
          )}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
    </MuiBox>
  );
};

export default ParticipantActivityTimeline;
